import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, Package } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface TokenInfo {
  valid: boolean;
  form_id: number;
  form_number: string;
  assignment_date: string;
  inventory_number: string;
  brand: string;
  model: string;
  user_id: number;
  user_name: string;
  user_email: string;
  expires_at: string;
  approval_status: string | null;
}

type PageState = 'loading' | 'invalid' | 'expired' | 'used' | 'superseded' | 'ready' | 'submitting' | 'done';

export default function AssignmentApprovalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [actionResult, setActionResult] = useState<{ action: string; message: string } | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('Geçersiz bağlantı: token bulunamadı.');
      return;
    }
    validateToken();
  }, [token]);

  // Geri sayım sayacı
  useEffect(() => {
    if (pageState !== 'ready' || !tokenInfo) return;
    const update = () => {
      const diff = Math.floor((new Date(tokenInfo.expires_at).getTime() - Date.now()) / 1000);
      if (diff <= 0) {
        setPageState('expired');
      } else {
        setTimeLeft(diff);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [pageState, tokenInfo]);

  const validateToken = async () => {
    setPageState('loading');
    try {
      const res = await fetch(`${API_BASE_URL}/approval-tokens/validate?token=${encodeURIComponent(token!)}`);

      if (res.status === 404) {
        setPageState('invalid');
        setErrorMessage('Bu bağlantı geçersiz veya bulunamadı.');
        return;
      }
      if (res.status === 410) {
        const data = await res.json();
        setPageState(data.superseded ? 'superseded' : 'expired');
        return;
      }
      if (res.status === 409) {
        const data = await res.json();
        setPageState('used');
        setActionResult({ action: data.action || 'approved', message: data.action === 'rejected' ? 'Bu zimmet daha önce reddedildi.' : 'Bu zimmet daha önce onaylandı.' });
        return;
      }
      if (!res.ok) {
        setPageState('invalid');
        setErrorMessage('Bağlantı doğrulanamadı.');
        return;
      }

      const data: TokenInfo = await res.json();
      setTokenInfo(data);
      setPageState('ready');
    } catch {
      setPageState('invalid');
      setErrorMessage('Sunucuya bağlanılamadı.');
    }
  };

  const handleRespond = async (action: 'approved' | 'rejected') => {
    if (action === 'rejected' && !showRejectForm) {
      setShowRejectForm(true);
      return;
    }

    setPageState('submitting');
    try {
      const res = await fetch(`${API_BASE_URL}/approval-tokens/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action,
          notes: action === 'rejected' ? rejectionNotes : undefined
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setPageState('ready');
        setErrorMessage(data.error || 'İşlem gerçekleştirilemedi.');
        return;
      }

      setActionResult({ action, message: data.message });
      setPageState('done');
    } catch {
      setPageState('ready');
      setErrorMessage('Sunucuya bağlanılamadı.');
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });

  const formatTimeLeft = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-indigo-600 px-6 py-5 text-white text-center">
            <Package className="w-8 h-8 mx-auto mb-2" />
            <h1 className="text-xl font-bold">Zimmet Onayı</h1>
          </div>

          <div className="p-6">
            {/* Loading */}
            {(pageState === 'loading' || pageState === 'submitting') && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
                <p className="text-gray-600">
                  {pageState === 'submitting' ? 'İşleminiz kaydediliyor...' : 'Yükleniyor...'}
                </p>
              </div>
            )}

            {/* Invalid / Error */}
            {pageState === 'invalid' && (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Geçersiz Bağlantı</h2>
                <p className="text-gray-600">{errorMessage}</p>
              </div>
            )}

            {/* Expired */}
            {pageState === 'expired' && (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Bağlantı Süresi Dolmuş</h2>
                <p className="text-gray-600">Bu onay bağlantısının 5 dakikalık geçerlilik süresi dolmuştur.</p>
                <p className="text-gray-500 text-sm mt-2">Lütfen IT departmanından yeni bir onay bağlantısı talep edin.</p>
              </div>
            )}

            {/* Superseded */}
            {pageState === 'superseded' && (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Bağlantı Geçersiz</h2>
                <p className="text-gray-600">Bu bağlantı artık geçerli değil. Daha yeni bir onay e-postası gönderilmiş olabilir.</p>
              </div>
            )}

            {/* Already used */}
            {pageState === 'used' && (
              <div className="text-center py-8">
                {actionResult?.action === 'rejected' ? (
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                ) : (
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                )}
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Daha Önce Yanıtlandı</h2>
                <p className="text-gray-600">{actionResult?.message}</p>
              </div>
            )}

            {/* Ready to respond */}
            {pageState === 'ready' && tokenInfo && (
              <div>
                <p className="text-gray-700 mb-4">
                  Merhaba <strong>{tokenInfo.user_name}</strong>, aşağıdaki cihaz size zimmet olarak atanmıştır. Lütfen inceleyerek onaylayın veya reddedin.
                </p>

                {/* Form details */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="px-4 py-3 font-medium text-gray-600 bg-gray-100 w-2/5">Form No</td>
                        <td className="px-4 py-3 text-gray-900 font-mono">{tokenInfo.form_number}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-4 py-3 font-medium text-gray-600 bg-gray-100">Cihaz</td>
                        <td className="px-4 py-3 text-gray-900">{tokenInfo.brand} {tokenInfo.model}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-4 py-3 font-medium text-gray-600 bg-gray-100">Envanter No</td>
                        <td className="px-4 py-3 text-gray-900">{tokenInfo.inventory_number || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-medium text-gray-600 bg-gray-100">Zimmet Tarihi</td>
                        <td className="px-4 py-3 text-gray-900">{tokenInfo.assignment_date ? formatDate(tokenInfo.assignment_date) : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Countdown */}
                {timeLeft !== null && (
                  <div className={`flex items-center justify-center gap-2 mb-4 px-4 py-2 rounded-lg text-sm font-semibold ${timeLeft <= 60 ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'}`}>
                    <Clock className="w-4 h-4" />
                    Kalan süre: {formatTimeLeft(timeLeft)}
                  </div>
                )}

                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {errorMessage}
                  </div>
                )}

                {/* Rejection notes form */}
                {showRejectForm && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reddetme Nedeni (isteğe bağlı)
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                      placeholder="Reddetme nedeninizi belirtebilirsiniz..."
                      value={rejectionNotes}
                      onChange={e => setRejectionNotes(e.target.value)}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                  {!showRejectForm ? (
                    <>
                      <button
                        onClick={() => handleRespond('approved')}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Onayla
                      </button>
                      <button
                        onClick={() => handleRespond('rejected')}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                      >
                        <XCircle className="w-5 h-5" />
                        Reddet
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleRespond('rejected')}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                      >
                        <XCircle className="w-5 h-5" />
                        Reddi Onayla
                      </button>
                      <button
                        onClick={() => { setShowRejectForm(false); setErrorMessage(''); }}
                        className="px-4 py-3 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-xl transition-colors"
                      >
                        İptal
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Done */}
            {pageState === 'done' && actionResult && (
              <div className="text-center py-8">
                {actionResult.action === 'approved' ? (
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                ) : (
                  <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                )}
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {actionResult.action === 'approved' ? 'Zimmet Onaylandı' : 'Zimmet Reddedildi'}
                </h2>
                <p className="text-gray-600">{actionResult.message}</p>
                <p className="text-gray-400 text-sm mt-4">Bu pencereyi kapatabilirsiniz.</p>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">IT Toolbox &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
